import React, { useState, useEffect } from "react";
import { apiGet } from "../../services/api";
import ManagerDaySummary from "./ManagerDaySummary";
import Button from "../common/Button"; // Use centralized Button
import "./CalendarView.css";

interface DayStats {
  billed: number;
  collected: number;
  balance: number;
  billCount: number;
  split: Record<string, number>;
}

export default function CalendarView() {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [stats, setStats] = useState<DayStats>({
    billed: 0, collected: 0, balance: 0, billCount: 0, split: {}
  });
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!date) return;
    let mounted = true;
    setLoading(true);

    apiGet(`/api/manager/day-summary?date=${date}`)
      .then((value: any) => {
        if (!mounted) return;
        const bills = value.bills || [];
        const payments = value.payments || [];

        const billed = bills.reduce((s: number, b: any) => s + (b.net || 0), 0);
        const collected = payments.reduce((s: number, p: any) => s + (p.amount || 0), 0);

        const split: Record<string, number> = {};
        payments.forEach((p: any) => {
          const m = p.mode || "Cash";
          split[m] = (split[m] || 0) + (p.amount || 0);
        });

        setStats({ billed, collected, balance: billed - collected, billCount: bills.length, split });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [date]);

  // Logical Fix: Safe maxSplit calculation
  const splitValues = Object.values(stats.split);
  const maxSplit = splitValues.length > 0 ? Math.max(...splitValues) : 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', background: '#fff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'var(--mod-shadow-sm)' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>📅 Daily Pulse</h3>
        <input 
            type="date" 
            value={date} 
            onChange={(e) => setDate(e.target.value)} 
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }} 
        />
      </div>

      {loading ? (
        <div style={{ color: '#94a3b8', padding: '20px 0' }}>Loading financials...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Billed</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a' }}>₹{stats.billed.toLocaleString()}</div>
          </div>
          <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: '0.75rem', color: '#166534', textTransform: 'uppercase', fontWeight: 700 }}>Collected</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#15803d' }}>₹{stats.collected.toLocaleString()}</div>
          </div>
          <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '12px', border: '1px solid #fecaca' }}>
            <div style={{ fontSize: '0.75rem', color: '#991b1b', textTransform: 'uppercase', fontWeight: 700 }}>Deficit</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#dc2626' }}>₹{Math.max(0, stats.balance).toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Split Bars */}
      {!loading && Object.keys(stats.split).length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <h4 style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', marginBottom: '12px' }}>Collection Split</h4>
          {Object.entries(stats.split).map(([mode, amt]) => (
            <div key={mode} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', fontSize: '0.85rem' }}>
              <span style={{ width: '80px', fontWeight: 600 }}>{mode}</span>
              <div style={{ flex: 1, background: '#f1f5f9', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#0ea5e9', width: `${(amt / maxSplit) * 100}%`, borderRadius: '4px' }} />
              </div>
              <strong style={{ width: '80px', textAlign: 'right' }}>₹{amt.toLocaleString()}</strong>
            </div>
          ))}
        </div>
      )}

      <Button variant="outline" fullWidth onClick={() => setShowDetails(true)} style={{ marginTop: '8px' }}>
        📄 View Detailed Report
      </Button>

      {showDetails && <ManagerDaySummary date={date} onClose={() => setShowDetails(false)} />}
    </div>
  );
}
